import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from "@angular/core";
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DatatableComponent, NgxDatatableModule, SortDirection } from "@swimlane/ngx-datatable";
import { NgxDradDropDirective } from '../../directives/ngx-dragdrop.directive';
import { DatalistActionItem } from "../../models/datalist-action-item.interface";
import { LocalStorageService } from "../../services/local-storage.service";
import { PermissionService } from "../../services/auth/permission.service";

@Component({
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInput,
    NgxDatatableModule,
    NgxDradDropDirective,
    FormsModule,
    TranslateModule
  ],
  selector: "app-draggable-table",
  standalone: true,
  styleUrls: ["./draggable-table.component.scss"],
  templateUrl: "./draggable-table.component.html"
})
export class DraggableTableComponent implements OnChanges, OnInit {
  rows: any[] = [];
  columnList: any[] = [];
  columnMapping: any[] = [];
  actionItems: DatalistActionItem[];
  loadingIndicator = true;
  isEditable: any = {};
  editingItemCount = 0;
  editedItemOriginalData: any[] = [];
  showTable: boolean = true;
  @ViewChild("datatable")
  public datatable: DatatableComponent;
  @Input()
  columns: any[];
  @Input() data: any[];
  // BUG-078: opt out of the drag-to-reorder affordance. The grip handle is only worth rendering
  // where the drop actually reorders anything; a table that offers other reorder controls sets
  // this to false so the handle is not shown at all (a handle that does nothing is worse than no
  // handle). Defaults to true, so every existing table keeps the behaviour it has today.
  @Input() public dragEnabled: boolean = true;
  // RBAC: permission required to drag-reorder rows. When set, a role without it cannot reorder —
  // the grip is hidden and onDrop is a no-op. Optional/backward compatible (unset = no gate).
  @Input() public dragRequiredPermission?: string;
  @Input() tableIdentifier: string = null;
  @Input() footerHeight: number = 50;
  sorts: any = [];
  @Input() actions: DatalistActionItem[];
  @Output() callbackOnDrop: EventEmitter<any> = new EventEmitter<any>();
  @Output() callbackOnUpdate: EventEmitter<any> = new EventEmitter<any>();
  @Output() callbackEmit = new EventEmitter();
  @Output() sortEvent = new EventEmitter();

  constructor(
    public router: Router,
    public localStorage: LocalStorageService,
    public translate: TranslateService,
    private permissionService: PermissionService
  ) {}

  version = "1.24";

  /**
   * RBAC gate for a row action, mirroring DatatableComponent.shouldShowAction: an action with no
   * requiredPermission always shows; otherwise it is shown while the permission set has not loaded
   * (fail-OPEN, isReady === false) and then only if the caller holds it. UX only — the API is the
   * real check. This was the gap that let a read-only role edit/delete/reorder rows here.
   */
  public shouldShowAction(action: DatalistActionItem): boolean {
    if (!action || !action.requiredPermission) {
      return true;
    }

    if (!this.permissionService.isReady) {
      return true;
    }

    return this.permissionService.has(action.requiredPermission);
  }

  /**
   * Whether the drag-to-reorder affordance may act. When dragRequiredPermission is set, a role that
   * lacks it cannot reorder (the grip is hidden and onDrop is a no-op). Fail-OPEN until load.
   */
  public get canReorder(): boolean {
    if (!this.dragRequiredPermission) {
      return true;
    }

    if (!this.permissionService.isReady) {
      return true;
    }

    return this.permissionService.has(this.dragRequiredPermission);
  }

  loaderVisibility(value: boolean) {
    this.loadingIndicator = value;
  }

  reset() {
    this.showTable = false;
    setTimeout(() => {
      this.showTable = true;
    });
  }

  onColumnSort(a: any) {
    if (!a.sorts || !a.sorts.length) {
      return;
    }
    a.sorts.forEach((b: any): void => {
      if (b.dir.toLowerCase() === "asc") {
        b.dir = SortDirection.asc;
      } else {
        b.dir = SortDirection.desc;
      }
    });
    this.localStorage.set(
      "tablesort_" + this.version + this.tableIdentifier,
      JSON.stringify(a.sorts)
    );
    if (!this.datatable) {
      return;
    }
    this.datatable.onColumnSort(a);
  }

  onEdit(index: number): void {
    // @ts-ignore
    this.isEditable[index] = true;
    this.editingItemCount++;
    this.editedItemOriginalData[index] = { ...this.rows[index] };
  }

  onCancelEdit(index: number): void {
    this.isEditable[index] = false;
    this.editingItemCount--;
    this.rows[index] = { 
      ...this.editedItemOriginalData[index],
      order: this.rows[index].order // Preserve the current order
    };
    this.rows = [...this.rows];
    this.sortByOrder();
  }

  onSort(event: any) {
    //console.log("Sort event triggered. Ignoring it.");
  }

  updateRows(data: any[]) {
    if (!data) return;

    // Explicitly sort by `order`
    this.rows = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));

    // *** FORCE ngx-datatable to completely refresh ***
    this.showTable = false; // Hide table first
    setTimeout(() => {
        this.showTable = true; // Show it again
        if (this.datatable) {
            this.datatable.rows = [...this.rows]; // Ensure ngx-datatable refreshes
        }
    }, 0);
  }



  private sortByOrder() {
    this.rows.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  onDrop(event: any) {
    if (!event?.updatedModel) return;

    // RBAC UX guard (the API re-authorizes): a role that cannot reorder must not persist a drag.
    // The grip is also hidden for such a role; this snaps the table back if a drop still fires.
    if (!this.canReorder) {
      this.reset();
      return;
    }

    this.loadingIndicator = true;

    // Sort explicitly by order
    this.rows = [...event.updatedModel].sort((a, b) => a.order - b.order);

    // Ensure table updates
    if (this.datatable) {
        this.datatable.rows = [...this.rows];
    }

    setTimeout(() => {
        this.loadingIndicator = false;
        this.callbackOnDrop.emit(event);
    }, 50);
  }


  forceRefresh() {
    this.showTable = false;
    setTimeout(() => {
        this.showTable = true;
        if (this.datatable) {
            this.datatable.rows = [...this.rows];
        }
    }, 0);
  }

  ngOnInit() {
    this.rows = this.data;
    this.columnList = this.columns;
    this.actionItems = this.actions;
    this.setupColumns();
  }

  ngOnChanges() {
    this.setupColumns();
    this.setupSort();
  }

  setupSort() {
    // Ignore previously stored sorts
    this.sorts = [];

    this.sortEvent.emit({ sorts: [] }); // Ensure no stored sorting overrides
  }

  setupColumns() {
    let columnMapping;
    if (!this.tableIdentifier) {
      this.columnList = this.columns;
      this.columnMapping = this.columns;
    }
    let oldColumns: any = this.columns;
    let columnMappingString = this.localStorage.get(
      "tablecolumns_" + this.tableIdentifier
    );
    if (!columnMappingString) {
      columnMapping = this.columns;
      this.localStorage.set(
        "tablecolumns_" + this.tableIdentifier,
        JSON.stringify(columnMapping)
      );
    } else {
      columnMapping = JSON.parse(columnMappingString);
    }
    // Apply new titles if they are there
    columnMapping = columnMapping.map((m: any): any => {
      return {
        ...m,
        name: oldColumns.find((f: any) => f.prop === m.prop).name,
      };
    });
    this.columnList = columnMapping.filter((c: any): boolean => !c.disabled);
    this.columnMapping = columnMapping;

    if (this.rows?.length) {
      this.sortByOrder();
    }
  }

  toggleColumn(col: any) {
    let columnMappingString = this.localStorage.get(
      "tablecolumns_" + this.tableIdentifier
    );
    let columnMapping = JSON.parse(columnMappingString);
    columnMapping.forEach((c: any): void => {
      if (c.name == col.name) {
        if (c.disabled) {
          c.disabled = false;
        } else {
          c.disabled = true;
        }
      }
    });
    this.columnMapping = columnMapping;
    this.columnList = columnMapping.filter((c: any): boolean => !c.disabled);
    this.localStorage.set(
      "tablecolumns_" + this.tableIdentifier,
      JSON.stringify(columnMapping)
    );
  }

  onActionClick(item: DatalistActionItem, id: any) {
    if (item.route) {
      this.router.navigate([item.route]);
    } else if (item.callback) {
      this.callbackEmit.emit(item.callback(id));
    }
  }

  saveData(row: any, rowIndex: any) {
    this.callbackOnUpdate.emit(row);
    // @ts-ignore
    this.isEditable[rowIndex] = false;
  }
}
